import { useChatStore } from '@/store/chatStore'
import { logWhatsAppHandoff } from '@/services/api'

// ─────────────────────────────────────────────────────────────────────────────
// useWhatsAppHandoff
// Opens the business's WhatsApp with the visitor's question pre-filled, then
// logs the handoff so the lead is captured even if the visitor never presses
// send in WhatsApp.
//
// CRITICAL: window.open() must run SYNCHRONOUSLY inside the click handler.
// Calling it after an await gets blocked by popup blockers, so the ref code is
// generated client-side and the API call is fired afterwards, not awaited.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_QUOTED = 180

export function useWhatsAppHandoff() {
  const config         = useChatStore((s) => s.config)
  const whatsAppNumber = useChatStore((s) => s.whatsAppNumber)

  const canWhatsApp = Boolean(whatsAppNumber && config?.domainId)

  function openWhatsApp(): void {
    const { messages, currentSessionId } = useChatStore.getState()
    if (!whatsAppNumber || !config?.domainId) return

    const refCode = Math.random().toString(36).slice(2, 8).toUpperCase()

    const lastQuestion = [...messages].reverse().find((m) => m.role === 'user')?.content ?? ''
    const quoted = lastQuestion.length > MAX_QUOTED
      ? `${lastQuestion.slice(0, MAX_QUOTED)}…`
      : lastQuestion

    // Keep the prefill short — long blobs look like spam and get deleted before sending.
    const text = quoted
      ? `Hi, I was asking your assistant: "${quoted}" (Ref #${refCode})`
      : `Hi, I was chatting with your assistant on your website. (Ref #${refCode})`

    // Synchronous — must not be preceded by an await.
    window.open(
      `https://wa.me/${whatsAppNumber}?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener,noreferrer',
    )

    // The widget runs inside an iframe, so document.referrer is the host page —
    // window.location would just be the widget's own URL.
    const pageUrl = document.referrer || window.location.href

    void logWhatsAppHandoff(config.domainId, {
      sessionId:    currentSessionId,
      refCode,
      lastQuestion,
      messageCount: messages.length,
      pageUrl,
    }).catch(() => {
      // Losing the log must never surface to the visitor — they're already in WhatsApp.
    })
  }

  return { canWhatsApp, openWhatsApp }
}
